package gr.tech.site.pages.states

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.objectFit
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.navigation.OpenLinkStrategy
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.site.components.layouts.PageLayoutData
import gr.tech.site.components.sections.RelatedSection
import gr.tech.site.components.states.FaqAccordion
import gr.tech.site.components.states.FaqItem
import gr.tech.site.components.states.UtilityCardData
import gr.tech.site.components.states.UtilityPartnerGrid
import gr.tech.site.styles.Theme
import gr.tech.site.styles.landings.*
import gr.tech.site.styles.states.CaliforniaHeroTextStyle
import gr.tech.site.styles.states.StateStatIconStyle
import gr.tech.site.styles.states.StateStatLabelStyle
import gr.tech.site.styles.states.StateStatNumberStyle
import gr.tech.site.util.ImageRepo
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.*

@InitRoute
fun initCaliforniaPage(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData(
        showContactHeader = true,
        title = "California — LED Grow Light & Cannabis Cultivation Rebates",
        description = "SCE, PG&E, SDGE, and PSE offer utility rebates for LED horticulture lighting and HVAC in California cannabis cultivation facilities. Green Rebates handles every application so you capture every dollar.",
        canonicalUrl = "https://greenrebates.com/states/california",
        ogImage = "/images/ledgrowlights.jpg"
    ))
}

@Page()
@Layout(".components.layouts.PageLayout")
@Composable
fun CaliforniaPage() {
    Main(
        attrs = Modifier
            .classNames("d-flex", "flex-column")
            .fillMaxSize()
            .role("main")
            .toAttrs()
    ) {

        // HERO
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(560.px)
                .position(Position.Relative)
                .classNames("px-0"),
            contentAlignment = Alignment.Center
        ) {
            Img(
                src = ImageRepo.LEDGrowLights.src,
                alt = ImageRepo.LEDGrowLights.alt,
                attrs = Modifier.fillMaxSize().toAttrs {
                    attr("fetchpriority", "high")
                    style { objectFit(ObjectFit.Cover) }
                }
            )
            Div(
                attrs = Modifier
                    .fillMaxSize()
                    .backgroundColor(Colors.Black)
                    .opacity(35.percent)
                    .position(Position.Absolute)
                    .top(0.px).left(0.px)
                    .toAttrs()
            )
            Column(
                modifier = LandingHeroStyle.toModifier().classNames("container", "text-start").fillMaxWidth(),
                horizontalAlignment = Alignment.Start
            ) {
                H3(
                    attrs = LandingHeroEyebrowStyle.toModifier()
                        .then(CaliforniaHeroTextStyle.toModifier())
                        .classNames("text-uppercase", "mb-2").toAttrs()
                ) {
                    Text("California Cannabis & Cultivation Rebates")
                }
                H1(
                    attrs = LandingHeroHeadlineStyle.toModifier()
                        .then(CaliforniaHeroTextStyle.toModifier())
                        .classNames("mb-3").toAttrs()
                ) {
                    Text("California LED Grow Light and Cannabis Cultivation Rebates")
                }
                P(
                    attrs = LandingHeroLeadStyle.toModifier()
                        .then(CaliforniaHeroTextStyle.toModifier())
                        .classNames("mb-4", "fw-medium", "text-white").toAttrs()
                ) {
                    Text("California is the largest legal cannabis market in the country — and some of the most generous utility rebate programs are available right here. Green Rebates works directly with SCE, PG&E, SDGE, and PSE to help California cultivators claim every dollar they're owed.")
                }
                Link(
                    path = "/contact-us?state=California",
                    text = "Get Your Free California Rebate Estimate →",
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                    modifier = LandingCtaSecondaryStyle.toModifier()
                        .role("button")
                        .attrsModifier { attr("aria-label", "Get a free California cannabis cultivation rebate estimate") },
                    openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                    openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                )
            }
        }

        // CALIFORNIA'S REBATE OPPORTUNITY
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .backgroundColor(Theme.LightGray.rgb)
                .toAttrs()
        ) {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                Div(attrs = Modifier.classNames("row", "align-items-center", "g-4").toAttrs()) {
                    Div(
                        attrs = Modifier
                            .classNames("col-lg-6", "text-lg-start", "text-center")
                            .toAttrs()
                    ) {
                        Div(
                            attrs = Modifier
                                .classNames("d-flex", "flex-column", "align-items-lg-start", "align-items-center", "gap-3")
                                .toAttrs()
                        ) {
                            H2(attrs = LandingSubSectionHeadingStyle.toModifier().toAttrs()) {
                                Text("Why California Cultivators Leave Millions in Rebates Unclaimed")
                            }
                            P(attrs = LandingMutedBodyStyle.toModifier().classNames("mb-0").toAttrs()) {
                                Text("California has the highest electricity rates in the continental US — which means energy upgrades carry more weight here than anywhere else. Combine that with robust utility rebate programs from SCE, PG&E, SDGE, and PSE, and California cannabis cultivators have a significant financial opportunity sitting untapped in every grow room.")
                            }
                            P(attrs = LandingMutedBodyStyle.toModifier().classNames("mb-0").toAttrs()) {
                                Text("The problem is that California's utility rebate applications are complex, time-sensitive, and require pre-approval before equipment is purchased. Most cannabis operations miss out because they don't know where to start — or buy equipment before locking in their spot in the program.")
                            }
                            P(attrs = LandingMutedBodyStyle.toModifier().classNames("mb-0").toAttrs()) {
                                Text("Green Rebates navigates California's utility programs on your behalf — from SCE's commercial efficiency programs in Southern California to PG&E's horticulture incentives in the Bay Area and Central Valley. We handle the applications so you capture the full rebate.")
                            }
                            Link(
                                path = "/contact-us?state=California",
                                text = "See What Your California Facility Qualifies For →",
                                variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                                modifier = LandingCtaSecondaryStyle.toModifier()
                                    .classNames("mt-2")
                                    .role("button")
                                    .attrsModifier { attr("aria-label", "Find out what California cannabis rebates your facility qualifies for") },
                                openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                                openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                            )
                        }
                    }
                    Div(attrs = Modifier.classNames("col-lg-6").toAttrs()) {
                        Img(
                            src = ImageRepo.CannabisPhoto.src,
                            alt = ImageRepo.CannabisPhoto.alt,
                            attrs = Modifier.classNames("img-fluid", "rounded-4", "shadow", "w-100")
                                .toAttrs { attr("loading", "lazy") }
                        )
                    }
                }
            }
        }

        // TRUSTED UTILITY PARTNERS
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "pt-5", "pb-3")
                .id("utilities")
                .toAttrs()
        ) {
            Div(attrs = Modifier.classNames("container", "pb-1").toAttrs()) {
                H2(
                    attrs = LandingSectionHeadingStyle.toModifier()
                        .classNames("mb-2")
                        .toAttrs()
                ) {
                    Text("California Utility Partners")
                }
                P(
                    attrs = LandingSectionSubtitleStyle.toModifier()
                        .classNames("mb-5")
                        .toAttrs()
                ) {
                    Text("Green Rebates works directly with California's leading utilities to identify every available rebate for your cannabis or horticulture operation. Each utility has distinct programs, requirements, and rebate structures — we manage the details so you get the maximum payout.")
                }

                UtilityPartnerGrid(listOf(
                    UtilityCardData(utilityName = "SCE", state = "California", utilityParam = "SCE"),
                    UtilityCardData(utilityName = "PG&E", state = "California", utilityParam = "PGE"),
                    UtilityCardData(utilityName = "PSE", state = "California", utilityParam = "PSE"),
                    UtilityCardData(utilityName = "SDGE", state = "California", utilityParam = "SDGE")
                ))
            }
        }

        // HOW IT WORKS IN CALIFORNIA
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .backgroundColor(Theme.LightGray.rgb)
                .toAttrs()
        ) {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                H2(
                    attrs = LandingSectionHeadingStyle.toModifier()
                        .classNames("mb-2").toAttrs()
                ) {
                    Text("How Green Rebates Secures California Cultivation Incentives")
                }
                P(
                    attrs = LandingSectionSubtitleStyle.toModifier()
                        .classNames("mb-3").toAttrs()
                ) {
                    Text("California's utility programs require careful timing and documentation. We handle all of it.")
                }

                Div(attrs = Modifier.classNames("row", "row-cols-1", "row-cols-md-3", "g-4", "mb-3").toAttrs()) {
                    listOf(
                        Triple("1", "Share Your California Facility Details",
                            "Tell us which utility serves your grow operation — SCE, PG&E, SDGE, or PSE — and what equipment you're planning to upgrade. We assess your eligibility and identify every available rebate program at no cost."),
                        Triple("2", "We Handle Pre-Approval and Applications",
                            "California utility programs require pre-approval before equipment is purchased. We submit your application, manage all documentation, and communicate directly with the utility on your behalf — so you never miss a step."),
                        Triple("3", "You Collect Your California Rebate Check",
                            "Once approved, your rebate check arrives without any additional effort from your team. LED grow lights, HVAC, dehumidifiers — we manage everything from start to payout.")
                    ).forEach { (num, title, desc) ->
                        Div(attrs = Modifier.classNames("col").toAttrs()) {
                            Div(
                                attrs = Modifier
                                    .classNames("d-flex", "flex-column", "align-items-center", "text-center", "gap-3", "h-100", "py-4", "px-3")
                                    .toAttrs()
                            ) {
                                Div(
                                    attrs = LandingStepCardNumberStyle.toModifier()
                                        .classNames("d-flex", "align-items-center", "justify-content-center")
                                        .toAttrs()
                                ) { Text(num) }
                                H4(
                                    attrs = LandingStepCardTitleStyle.toModifier()
                                        .classNames("mb-1")
                                        .toAttrs()
                                ) { Text(title) }
                                P(
                                    attrs = LandingStepCardBodyStyle.toModifier()
                                        .classNames("mb-0")
                                        .toAttrs()
                                ) { Text(desc) }
                            }
                        }
                    }
                }

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.Center
                ) {
                    Link(
                        path = "/contact-us?state=California",
                        text = "Start Your California Rebate Application →",
                        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                        modifier = LandingCtaBlackOutlineStyle.toModifier().classNames("mx-auto")
                            .role("button")
                            .attrsModifier { attr("aria-label", "Start your California cannabis cultivation rebate application") },
                        openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                        openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                    )
                }
            }
        }

        // WHY CALIFORNIA GROWERS CHOOSE GREEN REBATES
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .backgroundColor(Theme.DarkGreen.rgb)
                .toAttrs()
        ) {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                H2(
                    attrs = LandingDarkSectionHeadingStyle.toModifier()
                        .classNames("mb-5")
                        .toAttrs()
                ) {
                    Text("Why California Cannabis Growers Choose Green Rebates")
                }

                Div(attrs = Modifier.classNames("row", "row-cols-1", "row-cols-sm-2", "g-4", "mb-3").toAttrs()) {
                    listOf(
                        "California's Energy Costs Work in Your Favor" to
                            "California has among the highest commercial electricity rates in the country. That means every watt you save — and every rebate you capture — is worth more here than almost anywhere else. We make sure none of that opportunity goes unclaimed.",
                        "Deep Relationships With California Utilities" to
                            "Our direct relationships with SCE, PG&E, SDGE, and PSE mean faster pre-approvals, cleaner applications, and higher rebate payouts for your cultivation facility. We know each program's requirements inside out.",
                        "Cannabis Cultivation Is Our Specialty" to
                            "We work exclusively with cannabis cultivators, indoor growers, and horticulture facilities — not general energy consulting. We know which California programs apply to licensed grow operations and how to get them approved.",
                        "We Stack Every Available Program" to
                            "California utilities offer multiple concurrent programs for LED horticulture lighting, HVAC, and dehumidification. We identify every stacking opportunity and file all applications as a coordinated package to maximize your total rebate."
                    ).forEach { (title, desc) ->
                        Div(attrs = Modifier.classNames("col").toAttrs()) {
                            Div(attrs = Modifier.classNames("d-flex", "gap-3", "align-items-start").toAttrs()) {
                                Div(
                                    attrs = LandingCheckIconStyle.toModifier()
                                        .classNames("d-flex", "align-items-center", "justify-content-center", "flex-shrink-0")
                                        .toAttrs()
                                ) { FaCheck(size = IconSize.SM) }
                                Div {
                                    H5(attrs = LandingDarkItemTitleStyle.toModifier().classNames("mb-1").toAttrs()) {
                                        Text(title)
                                    }
                                    P(attrs = LandingDarkItemBodyStyle.toModifier().classNames("mb-0").toAttrs()) {
                                        Text(desc)
                                    }
                                }
                            }
                        }
                    }
                }

                P(attrs = LandingMutedWhiteBodyStyle.toModifier().classNames("text-center").toAttrs()) {
                    Text("All of this is powered by our close, trusted partnerships with California's leading utilities.")
                }
            }
        }

        // STATS + TESTIMONIALS
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .toAttrs()
        ) {
            H2(
                attrs = LandingSectionHeadingStyle.toModifier()
                    .classNames("mb-2")
                    .toAttrs()
            ) {
                Text("Real Results for California")
                SpanText(
                    text = " Cannabis",
                    modifier = Modifier.classNames("fw-italic")
                        .color(Theme.MediumGreen.rgb)
                )
                Text(" Growers")
            }

            P(
                attrs = LandingSectionSubtitleStyle.toModifier()
                    .classNames("mb-4")
                    .toAttrs()
            ) {
                Text("California cultivators are capturing substantial rebates on LED grow light and HVAC upgrades. Here's what that looks like in practice.")
            }

            Div(attrs = Modifier.classNames("container", "mb-5").toAttrs()) {
                Div(
                    attrs = Modifier
                        .classNames("row", "row-cols-1", "row-cols-md-3", "g-4", "g-lg-5", "justify-content-center")
                        .toAttrs()
                ) {
                    Div(attrs = Modifier.classNames("col", "d-flex", "flex-column", "align-items-center").toAttrs()) {
                        Div(attrs = Modifier.classNames("text-center").toAttrs()) {
                            FaDollarSign(modifier = StateStatIconStyle.toModifier(), size = IconSize.X2)
                            H3(attrs = StateStatNumberStyle.toModifier().toAttrs()) { Text("\$55M+") }
                            P(attrs = StateStatLabelStyle.toModifier().toAttrs()) { Text("Total Rebates Secured") }
                        }
                    }
                    Div(attrs = Modifier.classNames("col", "d-flex", "flex-column", "align-items-center").toAttrs()) {
                        Div(attrs = Modifier.classNames("text-center").toAttrs()) {
                            FaClipboardList(modifier = StateStatIconStyle.toModifier(), size = IconSize.X2)
                            H3(attrs = StateStatNumberStyle.toModifier().toAttrs()) { Text("400+") }
                            P(attrs = StateStatLabelStyle.toModifier().toAttrs()) { Text("Projects Completed") }
                        }
                    }
                    Div(attrs = Modifier.classNames("col", "d-flex", "flex-column", "align-items-center").toAttrs()) {
                        Div(attrs = Modifier.classNames("text-center").toAttrs()) {
                            FaBolt(modifier = StateStatIconStyle.toModifier(), size = IconSize.X2)
                            H3(attrs = StateStatNumberStyle.toModifier().toAttrs()) { Text("420M") }
                            P(attrs = StateStatLabelStyle.toModifier().toAttrs()) { Text("kWh Saved") }
                        }
                    }
                }
            }

            Div(attrs = Modifier.classNames("container").toAttrs()) {
                Div(attrs = Modifier.classNames("row", "row-cols-1", "row-cols-md-2", "g-4").toAttrs()) {
                    listOf(
                        "California's electricity costs made every upgrade feel expensive — until Green Rebates stepped in. They navigated the SCE and PG&E application process for our cannabis cultivation facility and secured over \$80,000 in LED grow light and HVAC rebates. Completely hands-off for us." to
                                "Cannabis Cultivator, Southern California",
                        "We had no idea our licensed cannabis grow operation qualified for this many incentive programs. Green Rebates handled the entire PG&E rebate application from pre-approval to payment. The checks were larger than we anticipated." to
                                "Licensed Cannabis Grower, Northern California"
                    ).forEach { (quote, attribution) ->
                        Div(attrs = Modifier.classNames("col").toAttrs()) {
                            Div(attrs = LandingTestimonialCardStyle.toModifier().toAttrs()) {
                                Span(
                                    attrs = LandingQuoteMarkStyle.toModifier()
                                        .classNames("d-block", "mb-1")
                                        .toAttrs()
                                ) { Text("“") }
                                P(attrs = LandingBodyStyle.toModifier().classNames("fst-italic", "mb-3").toAttrs()) {
                                    Text(quote)
                                }
                                Span(attrs = LandingAttributionStyle.toModifier().toAttrs()) {
                                    Text("— $attribution")
                                }
                            }
                        }
                    }
                }
            }
        }

        // CALIFORNIA FAQ
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .backgroundColor(Theme.DarkGreen.rgb)
                .toAttrs()
        ) {
            Div(attrs = Modifier.classNames("container", "py-5").toAttrs()) {
                H2(
                    attrs = LandingDarkSectionHeadingStyle.toModifier()
                        .classNames("mb-5")
                        .toAttrs()
                ) {
                    Text("FAQs About Cannabis Cultivation Rebates in California")
                }

                FaqAccordion(
                    accordionId = "california-faq",
                    items = listOf(
                        FaqItem(
                            question = "Who qualifies for California utility rebates on grow equipment?",
                            answer = "Licensed cannabis cultivators, indoor growers, greenhouse operators, and commercial horticulture facilities that pay utility bills to SCE, PG&E, SDGE, or PSE may qualify. California has some of the most established utility rebate programs for commercial operations in the country, and most cultivation facilities with meaningful LED lighting or HVAC loads are eligible."
                        ),
                        FaqItem(
                            question = "Which California utilities offer LED horticulture lighting rebates?",
                            answer = "SCE, PG&E, SDGE, and PSE all offer programs that include LED horticulture lighting upgrades for qualifying commercial facilities. Each utility has different rebate amounts, efficiency requirements, and application processes. Green Rebates works directly with all four to identify the highest-value programs for your specific California operation."
                        ),
                        FaqItem(
                            question = "Is licensed cannabis cultivation in California eligible for utility rebates?",
                            answer = "Yes. California's licensed cannabis cultivators qualify for utility rebate programs just like any other commercial horticulture operation. LED grow lights, HVAC systems, and dehumidification upgrades used in licensed cannabis facilities are commonly eligible through SCE, PG&E, SDGE, and PSE programs."
                        ),
                        FaqItem(
                            question = "Do I need pre-approval before purchasing equipment in California?",
                            answer = "Yes. Most California utility rebate programs through SCE, PG&E, SDGE, and PSE require pre-approval before equipment is purchased or installed. Buying equipment first can disqualify the project entirely — even if the equipment itself is eligible. Green Rebates manages pre-approval submissions and timing so your California facility never loses a rebate due to a procedural error."
                        ),
                        FaqItem(
                            question = "How much can a California cannabis facility get back in rebates?",
                            answer = "California cannabis cultivators have received anywhere from tens of thousands to over \$150,000 in a single project through Green Rebates. California's high energy costs and robust utility programs make it one of the highest-value rebate markets we work in. Rebate amounts vary by utility, equipment type, and project scope — contact us for a free estimate based on your specific operation."
                        )
                    )
                )
            }
        }

        RelatedSection(listOf(
            "/article/the-2026-guide-to-cannabis-rebates-how-to-get-paid-for-your-grow-upgrades" to "The 2026 Guide to Cannabis Rebates: How to Get Paid for Your Grow Upgrades",
            "/article/top-10-high-efficiency-led-rebates-for-commercial-cultivators-in-2026" to "Top 10 High-Efficiency LED Rebates for Commercial Cultivators in 2026",
            "/dlc-qualified-rebates" to "DLC Qualified LED Grow Light Rebates — What Cannabis Growers Need to Know",
        ))

        // FINAL CTA
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .id("contact")
                .backgroundColor(Theme.MediumGreen.rgb)
                .toAttrs()
        ) {
            Div(attrs = Modifier.classNames("container", "text-center").toAttrs()) {
                H2(
                    attrs = LandingCtaHeadingStyle.toModifier()
                        .classNames("mb-3")
                        .toAttrs()
                ) {
                    Text("Ready to Claim Your California Rebate?")
                }
                P(
                    attrs = LandingCtaBodyStyle.toModifier()
                        .classNames("mb-4", "mx-auto")
                        .toAttrs()
                ) {
                    Text("California's LED horticulture and cannabis cultivation rebate programs are available now — but funding windows close. Contact Green Rebates and we'll lock in your pre-approval before your spot is gone.")
                }
                Link(
                    path = "/contact-us?state=California",
                    text = "Get a Free California Rebate Estimate →",
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                    modifier = LandingCtaThirdStyle.toModifier()
                        .role("button")
                        .attrsModifier { attr("aria-label", "Contact Green Rebates for a free California cannabis cultivation rebate estimate") },
                    openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                    openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                )
            }
        }
    }
}
